import api from "@/api";
import deepClone from "@/utils/deep-clone";

import { alphabetically } from "@/utils/sort-object";

const filters = [
    {
        key: "type",
        value: null,
        apply(list) {
            if (!this.value) {
                return list;
            }
            return list.filter(item => item?.learningElementLatest[this.key] === this.value);
        },
    },
    {
        key: "sources",
        value: null,
        apply(list) {
            if (!this.value) {
                return list;
            }

            if (this.value === "open") {
                return list.filter(item => item.open);
            }

            return list.filter(item =>
                item[this.key]?.find(
                    source => source?.progressId?.learningPath?.commonId === this.value,
                ),
            );
        },
    },
];

const state = {
    list: [],
    selfenrolList: [],
    details: {},
    filters,
};

const getters = {
    list(state) {
        return deepClone(state.list);
    },
    selfenrolList(state) {
        return state.selfenrolList;
    },
    element(state) {
        return id => deepClone(state.details[id]);
    },
    hasElements(state) {
        return !!state.list.length;
    },
    filteredList(state) {
        const filteredList = state.filters.reduce((list, filter) => filter.apply(list), state.list);

        return deepClone(filteredList);
    },
    filters(state) {
        // Filter by type
        const uniqueTypes = [...new Set(state.list.map(item => item?.learningElementLatest?.type))];
        const filterByTypeOptions = [
            { value: "", label: "all" },
            ...uniqueTypes.map(type => ({ value: type, label: type })),
        ];
        const filterByType = {
            key: "type",
            options: filterByTypeOptions,
            value: state.filters.find(filter => filter.key === "type")?.value,
        };

        // Filter by sources
        const uniquePaths = state.list
            .map(item => (item.sources || []).map(source => source?.progressId?.learningPath))
            .filter(item => item.length)
            .flat()
            .filter(
                (path, index, paths) =>
                    index === paths.findIndex(p => p?.commonId === path?.commonId),
            );

        let filterSpecialSources = [{ value: "", label: "any" }];
        if (state.list.find(item => item.open)) {
            filterSpecialSources.push({ value: "open", label: "open" });
        }
        const filterBySourcesOptions = [
            ...filterSpecialSources,
            ...uniquePaths.map(path => ({
                value: path.commonId,
                label: "",
                text: path.name,
            })),
        ];

        const filterBySources = {
            key: "sources",
            options: filterBySourcesOptions,
            value: state.filters.find(filter => filter.key === "sources")?.value,
        };

        return deepClone([filterByType, filterBySources]);
    },
    search(state) {
        return query =>
            getters
                .filteredList(state)
                .filter(item =>
                    item.learningElementLatest?.name?.toLowerCase().includes(query.toLowerCase()),
                );
    },
};

const actions = {
    getList({ commit }) {
        return Promise.all([
            api.get("/api/learning/library"),
            api.get("/api/learning/library/open"),
        ]).then(([resLib, resOpen]) => {
            const listLib = (resLib.data && resLib.data.items ? resLib.data.items : []).map(
                item => ({
                    ...item,
                    open: false,
                }),
            );
            const listOpen = (resOpen && resOpen.data && resOpen.data.items
                ? resOpen.data.items
                : []
            ).map(item => ({
                ...item,
                open: true,
            }));
            commit("updateList", {
                // Apply default sorting before saving in `state`
                list: listOpen
                    .filter(
                        openElem =>
                            !listLib.find(
                                item =>
                                    item.learningElementLatest.commonId ===
                                    openElem.learningElementLatest.commonId,
                            ),
                    )
                    .concat(listLib)
                    .sort(alphabetically("learningElementLatest.name")),
            });
        });
    },
    getSelfenrolList({ commit }) {
        api.get("/api/learning/library/selfenrol").then(response => {
            commit("updateSelfenrolList", { list: response.data.items });
        });
    },
    getElement({ commit }, { payload: { id, type } }) {
        return api.get(`/api/learning/element/${type}/${id}`).then(res => {
            const element = res.data;
            commit("updateDetails", { element });
        });
    },
    filterBy({ commit, state }, { payload: { key, value } }) {
        const filters = state.filters.map(filter => {
            if (filter.key === key) {
                return { ...filter, value };
            }

            return filter;
        });

        commit("updateFilters", { filters });
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = [...list];
    },
    updateSelfenrolList(state, { list }) {
        state.selfenrolList = list;
    },
    updateDetails(state, { element }) {
        state.details = {
            ...state.details,
            [element.commonId]: element,
        };
    },
    updateFilters(state, { filters }) {
        state.filters = [...filters];
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export { filters as __test__filters };
